/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createApiClient,
  apiClient,
  getLocalTimestamp,
  getTimezoneOffsetString,
} from "../../../api/apiClient";
import { setLoading, setError } from "../../../store/slices/appDataSlice";
import { logger } from "../../../utils/logger";
import {
  setCIDashboardData,
  setCIDashboardLoading,
  setCIDashboardError,
  setLHExecutionData,
  setLHExecutionLoading,
  setLHExecutionError,
  clearLHExecutionCacheForLocation,
  // Training Essentials actions
  setTrainingEssentialsData,
  setTrainingEssentialsLoading,
  setTrainingEssentialsError,
  setEventsData,
  setEventsLoading,
  setEventsError,
  setExternalLinksData,
  setExternalLinksLoading,
  setExternalLinksError, // Change log actions
  setChangeLogData,
  setChangeLogLoading,
  setChangeLogError,
  // Announcement Banner actions
  setAnnouncementBannerData,
  setAnnouncementBannerLoading,
  setAnnouncementBannerError,
} from "../../../store/slices/homescreenSlice";
import { setHomepageChangeLogs, setHomepageEvents, setHomepageExternalLinks, setHomepageTrainingEssentials, mapUpcomingEventApiItem, mapExternalLinkApiItem, mapTrainingEssentialApiItem } from "../../../store/slices/adminSlice";

import { reportError } from "../../../utils/Instrumentation";
import { fetchCIDashboardData } from "../../../utils/dashboardUtils";
import React from "react";

// Module-level flag: announcement banner API is fetched only once per app session
let announcementBannerFetched = false;

// ─── Helper Type ──────────────────────────────────────────────────────────────
interface ChangeLogItem {
  id: number | string;
  logDetails: string;
  goLiveDate: string;
  createdBy?: string;
  createdAt?: string;
  modifiedAt?: string;
  changeDetails?: string;
}

// ─── Helper Functions ─────────────────────────────────────────────────────────
const toDisplayDate = (value: string | undefined): string => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
};

// Transform and sort changeLog data from API response to match configuration state structure
const transformAndSortChangeLogData = (rawData: unknown) => {
  if (!Array.isArray(rawData)) return [];
  
  return rawData
    .map((item: ChangeLogItem) => ({
      id: String(item.id),
      details: item.logDetails || item.changeDetails || '',
      goLiveDate: toDisplayDate(item.goLiveDate),
      createdBy: item.createdBy || 'System',
      createdDate: item.createdAt || item.goLiveDate || '',
      _goLiveDateRaw: item.goLiveDate ? new Date(item.goLiveDate).getTime() : 0, // For sorting
      _modifiedAtRaw: item.modifiedAt ? new Date(item.modifiedAt).getTime() : 0, // For secondary sorting
    }))
    .sort((a, b) => {
      const goLiveDiff = b._goLiveDateRaw - a._goLiveDateRaw;
      if (goLiveDiff !== 0) return goLiveDiff;
      return b._modifiedAtRaw - a._modifiedAtRaw; // Secondary: sort by modifiedAt descending
    })
    .map(({ _goLiveDateRaw, _modifiedAtRaw, ...item }) => item); // Remove the raw timestamp properties
};

/**
 * Custom hook to fetch data for the HomeScreen
 * Centralizes the data fetching logic outside the component
 */
const useHomeScreenData = () => {
  const dispatch = useDispatch();
  const { selectedLocation, periodWeek } = useSelector(
    (state: any) => state.appData
  );
  const {
    ciDashboardData,
    lhExecutionData,
    lhExecutionCache,
    loading,
    error,
    trainingEssentialsData,
    eventsData,
    externalLinksData,
    changeLogData,
    announcementBannerData,
  } = useSelector((state: any) => state.homescreen);

  // Create memoized API client
  const api = React.useMemo(() => createApiClient(), []);
  // Use ref to track if SharePoint content has been fetched to prevent duplicate calls
  const sharePointFetched = useRef({
    trainingEssentials: false,
    events: false,
    externalLinks: false,
    changeLog: false,
  });
  // Fetch initial data on component mount
  useEffect(() => {
    dispatch(setLHExecutionLoading(true));
  }, [dispatch]); // Fetch SharePoint content data once on component mount (not location-dependent)
  useEffect(() => {
    const fetchSharePointContent = async () => {
      const api = createApiClient();

      // Fetch all SharePoint content in parallel
      const fetchContentPromises = [];

      // Only fetch if we don't have the data already and haven't attempted to fetch it
      if (!trainingEssentialsData && !sharePointFetched.current.trainingEssentials) {
        sharePointFetched.current.trainingEssentials = true;
        fetchContentPromises.push(
          (async () => {
            dispatch(setTrainingEssentialsLoading(true));
            try {
              const trainingResponse = await apiClient(
                api,
                `/admin/homepage/training-essentials`,
                "GET"
              );
              logger.log("Training Essentials Data:", trainingResponse);
              dispatch(setTrainingEssentialsData(trainingResponse));
              // Sync to admin configuration state so the admin dashboard skips re-fetching
              const trainingArray = Array.isArray(trainingResponse) ? trainingResponse : (trainingResponse?.data ?? []);
              dispatch(setHomepageTrainingEssentials(trainingArray.map((item: any) => mapTrainingEssentialApiItem(item))));
            } catch (err: any) {
              console.error("Error fetching Training Essentials data:", err);
              dispatch(setTrainingEssentialsError(err.message));
              // Reset flag on error so it can be retried
              sharePointFetched.current.trainingEssentials = false;
            } finally {
              dispatch(setTrainingEssentialsLoading(false));
            }
          })()
        );
      }

      if (!eventsData && !sharePointFetched.current.events) {
        sharePointFetched.current.events = true;
        fetchContentPromises.push(
          (async () => {
            dispatch(setEventsLoading(true));
            try {
              const clientTimezone = getTimezoneOffsetString();
              const clientTimestamp = getLocalTimestamp();
              const eventsResponse = await apiClient(
                api,
                `/admin/homepage/upcoming-events?clientTimezone=${clientTimezone}&clientTimestamp=${clientTimestamp}`,
                "GET"
              );
              logger.log("Events Data:", eventsResponse);
              dispatch(setEventsData(eventsResponse));
              // Sync to admin configuration state so the admin dashboard skips re-fetching
              const eventsArray = Array.isArray(eventsResponse) ? eventsResponse : (eventsResponse?.data ?? []);
              dispatch(setHomepageEvents(eventsArray.map((item: any) => mapUpcomingEventApiItem(item))));
            } catch (err: any) {
              console.error("Error fetching Events data:", err);
              dispatch(setEventsError(err.message));
              // Reset flag on error so it can be retried
              sharePointFetched.current.events = false;
            } finally {
              dispatch(setEventsLoading(false));
            }
          })()
        );
      }
      if (!externalLinksData && !sharePointFetched.current.externalLinks) {
        sharePointFetched.current.externalLinks = true;
        fetchContentPromises.push(
          (async () => {
            dispatch(setExternalLinksLoading(true));
            try {
              const externalLinksResponse = await apiClient(
                api,
                `/admin/homepage/external-links`,
                "GET"
              );
              logger.log("External Links Data:", externalLinksResponse);
              dispatch(setExternalLinksData(externalLinksResponse));
              // Sync to admin configuration state so the admin dashboard skips re-fetching
              const linksArray = Array.isArray(externalLinksResponse) ? externalLinksResponse : (externalLinksResponse?.data ?? []);
              dispatch(setHomepageExternalLinks(linksArray.map((item: any) => mapExternalLinkApiItem(item))));
            } catch (err: any) {
              console.error("Error fetching External Links data:", err);
              dispatch(setExternalLinksError(err.message));
              // Reset flag on error so it can be retried
              sharePointFetched.current.externalLinks = false;
            } finally {
              dispatch(setExternalLinksLoading(false));
            }
          })()
        );
      }

      if (!changeLogData && !sharePointFetched.current.changeLog) {
        sharePointFetched.current.changeLog = true;
        fetchContentPromises.push(
          (async () => {
            dispatch(setChangeLogLoading(true));
            try {
              // Fetch from admin API instead of SharePoint
              const changeLogResponse = await apiClient(
                api,
                `/admin/homepage/change-logs`,
                "GET"
              );
              logger.log("Change Log Data:", changeLogResponse);
              
              // Transform and sort the changelog data to match configuration state structure
              // Handle both direct array and {data: array} response format
              const dataArray = Array.isArray(changeLogResponse) ? changeLogResponse : changeLogResponse?.data;
              const transformedData = transformAndSortChangeLogData(dataArray);
              const latestThreeRecords = transformedData.slice(0, 3);
              dispatch(setChangeLogData(latestThreeRecords));
              // Keep homepage configuration state in sync with Home screen API fetch
              dispatch(setHomepageChangeLogs(transformedData));
            } catch (err: any) {
              console.error("Error fetching Change Log data:", err);
              dispatch(setChangeLogError(err.message));
              // Reset flag on error so it can be retried
              sharePointFetched.current.changeLog = false;
            } finally {
              dispatch(setChangeLogLoading(false));
            }
          })()
        );
      }

      // Fetch active announcement banners from admin API (only once per app session)
      if (!announcementBannerFetched) {
        announcementBannerFetched = true;
        fetchContentPromises.push(
          (async () => {
            dispatch(setAnnouncementBannerLoading(true));
            try {
              const clientTimezone = getTimezoneOffsetString();
              const clientTimestamp = getLocalTimestamp();
              const announcementBannerResponse = await apiClient(
                api,
                `/admin/homepage/banners?status=active&clientTimezone=${clientTimezone}&clientTimestamp=${clientTimestamp}`,
                "GET"
              );
              logger.log("Announcement Banner Data:", announcementBannerResponse);
              dispatch(setAnnouncementBannerData(announcementBannerResponse));
            } catch (err: any) {
              console.error("Error fetching Announcement Banner data:", err);
              dispatch(setAnnouncementBannerError(err.message));
              // Reset flag on error so it can be retried
              announcementBannerFetched = false;
            } finally {
              dispatch(setAnnouncementBannerLoading(false));
            }
          })()
        );
      }

      // Execute all SharePoint content fetches in parallel
      if (fetchContentPromises.length > 0) {
        await Promise.all(fetchContentPromises);
      }
    };

    fetchSharePointContent();
  }, [dispatch]); // Only depend on dispatch, not on the data itself

  // Clear cache when period or week changes
  useEffect(() => {
    if (selectedLocation) {
      logger.log(
        "Period/week changed, clearing LH Execution cache for location:",
        selectedLocation.id
      );
      // When period/week changes, clear cache for the current location
      dispatch(clearLHExecutionCacheForLocation(selectedLocation.id));
    }
  }, [
    dispatch,
    selectedLocation,
    periodWeek?.data?.period,
    periodWeek?.data?.week,
  ]);

  // Fetch CI Dashboard and LH Execution data when dependencies change
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check if we have a selected location
        if (selectedLocation) {
          // Define functions to fetch each type of data
          const fetchCIDashboard = async () => {
            if (
              !ciDashboardData ||
              ciDashboardData._locationId !== selectedLocation.id
            ) {
              dispatch(setCIDashboardLoading(true));
              try {
                const ciResponse = await fetchCIDashboardData(selectedLocation);
                dispatch(setCIDashboardData(ciResponse));
              } catch (err: any) {
                dispatch(setCIDashboardError(err.message));
              } finally {
                dispatch(setCIDashboardLoading(false));
              }
            } else {
              logger.log(
                "Using cached CI Dashboard data for location:",
                selectedLocation.id
              );
            }
          };
          const fetchLHExecution = async () => {
            // Check if we have cached data for current location and CURRENT timeframe
            const cacheKey = `${selectedLocation.id}:CURRENT`;
            const cachedData = lhExecutionCache && lhExecutionCache[cacheKey];

            // Fetch LH Execution data - only if not already in cache or if data is invalid
            const shouldFetchLHData =
              !cachedData &&
              (!lhExecutionData ||
                !lhExecutionData.location ||
                lhExecutionData.location.locationId !== selectedLocation.id ||
                lhExecutionData.timeframe !== "CURRENT");

            if (shouldFetchLHData) {
              // No cached data available, need to fetch
              dispatch(setLHExecutionLoading(true));
              try {
                const lhExecutionResponse = await apiClient(
                  api,
                  `/home/lh-execution?location_name=${encodeURIComponent(
                    selectedLocation.displayText || selectedLocation.name
                  )}&timeframe=CURRENT`,
                  "GET"
                );
                // Add timestamp and source information to the response
                lhExecutionResponse._fetchedAt = getLocalTimestamp()
                lhExecutionResponse._fetchSource = "useHomeScreenData";
                logger.log("LH Execution Data:", lhExecutionResponse);
                dispatch(setLHExecutionData(lhExecutionResponse));
              } catch (err: any) {
                console.error("Error fetching LH Execution:", err);
                reportError({
                  severity: "ERROR",
                  message: err instanceof Error ? err.message : String(err),
                  stackTrace: err instanceof Error ? err.stack : undefined,
                });
                dispatch(setLHExecutionError(err.message));
              } finally {
                dispatch(setLHExecutionLoading(false));
              }
            } else if (cachedData) {
              // Use cached data
              logger.log(`Using cached LH Execution data for ${cacheKey}`);
              dispatch(setLHExecutionData(cachedData));
            } else {
              // Current data is valid
              logger.log(
                "Using current LH Execution data for location:",
                selectedLocation.id
              );
            }
          };

          // Execute location-dependent API calls in parallel
          await Promise.all([fetchCIDashboard(), fetchLHExecution()]);
        }
      } catch (err: any) {
        logger.error("General error:", err);
        reportError({
          severity: "ERROR",
          message: err instanceof Error ? err.message : String(err),
          stackTrace: err instanceof Error ? err.stack : undefined,
        });
        dispatch(setError(err.message));
      } finally {
        dispatch(setLoading(false));
      }
    };

    fetchData();
  }, [
    api,
    dispatch,
    selectedLocation,
    periodWeek?.data?.period,
    periodWeek?.data?.week,
  ]);
  return {
    ciDashboardData,
    lhExecutionData,
    loading,
    selectedLocation,
    trainingEssentialsData,
    eventsData,
    externalLinksData,
    changeLogData,
    announcementBannerData,
  };
};

export default useHomeScreenData;
